// Libraries
import React, { Component } from 'react';
import styled, { keyframes } from 'styled-components';
import { rem } from 'polished';

// Redux
import { connect } from 'react-redux';
import { setMobileMenuState } from '~/redux/actions/general-actions';

// Components
import MobileMenu from './mobile-menu';
import PageNavigation from './page-navigation';
import MenuButton from './menu-button';
import Branding from '~/components/general/branding';

const getScrollPosition = (el = window) => ({
	y: el.pageYOffset !== undefined ? el.pageYOffset : el.scrollTop,
});

const debounce = (func, wait) => {
	let timeout;
	return (...args) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => func.apply(this, args), wait);
	};
};

/**
 * Page Header
 *
 * @class Header
 * @extends {Component}
 */
class Header extends Component {
	constructor(props) {
		super(props);
		this.handleMenuButtonToggle = this.handleMenuButtonToggle.bind(this);
	}

	/**
	 * When the header component mounts, it adds an event listener to the scroll
	 *
	 * @returns
	 * @memberof Header
	 */
	componentDidMount() {
		return window.addEventListener('scroll', debounce(this.handleScroll, 16));
	}

	/**
	 * Removes the event listener from the component
	 *
	 * @returns
	 * @memberof Header
	 */
	componentWillUnmount() {
		return window.removeEventListener('scroll', debounce(this.handleScroll, 16));
	}

	/**
	 * When the Hamburguer menu is pressed
	 *
	 * @memberof Header
	 */
	handleMenuButtonToggle() {
		let mobileMenuState = this.props.isMobileMenuOpen;

		if (mobileMenuState === true) {
			this.props.dispatch(setMobileMenuState(false));
		} else {
			this.props.dispatch(setMobileMenuState(true));
		}
	}

	/**
	 * Handles the scrolling distance and the fixed-nav functionality
	 *
	 * @returns
	 * @memberof Header
	 */
	handleScroll() {
		const nav = document.querySelector('.utilities--above-the-fold');
		const navbarSelector = document.querySelector('#page-header');
		if (nav && navbarSelector) {
			const navbarHeight = navbarSelector.offsetHeight * 2;
			let ScrollPosition = getScrollPosition();
			if (ScrollPosition.y >= 48) {
				document.body.classList.add('fixed-nav');
			} else if (ScrollPosition.y < navbarHeight) {
				document.body.classList.remove('fixed-nav');
			}
		} else {
			return null;
		}

		return false;
	}

	/**
	 * Renders the current page title if there is an entry on the redux store
	 *
	 * @returns
	 * @memberof Header
	 */
	renderCurrentPageTitle() {
		const currentPageTitle = this.props.currentPageTitle;

		const displayCurrentPageTitle =
			currentPageTitle.length > 0 ? (
				<Center id="header-center">
					<h1 className="header-page-title">{currentPageTitle}</h1>
				</Center>
			) : null;

		return displayCurrentPageTitle;
	}

	/**
	 * Renders the component
	 *
	 * @returns
	 * @memberof Header
	 */
	render() {
		const isNotFixed = !!this.props.isNotFixed;
		let mobileMenuState = this.props.isMobileMenuOpen;
		let menuClass;

		if (mobileMenuState === true) {
			menuClass = 'is-open';
		} else {
			menuClass = '';
		}
		const { photoUrl, contactEmail } = this.props.profile;
		return (
			<Wrapper isNotFixed={isNotFixed} id="page-header" role="banner" ref="navbar" className={`${menuClass}`}>
				<Row className="l__row">
					<Left id="header-left">
						<figure className="branding">
							<Branding />
						</figure>
					</Left>
					{this.renderCurrentPageTitle()}
					<Right id="header-right">
						<MenuButton
							onClick={this.handleMenuButtonToggle}
							hasIconAlert={contactEmail === null && !mobileMenuState}
							className={`${menuClass}`}
						/>
						<PageNavigation photoUrl={photoUrl} avatarHasIconAlert={contactEmail === null} />
					</Right>
				</Row>
				<MobileMenu hasIconAlert={contactEmail === null} className={`${menuClass}`} />
			</Wrapper>
		);
	}
}

// Styled Components
const Wrapper = styled.header`
	background-color: transparent;
	max-height: ${props => props.theme.navbarHeightSmall};
	z-index: 100;
	position: ${props => (props.isNotFixed ? 'relative' : 'fixed')};
	width: 100%;

	&.is-open {
		margin-top: 0;
		position: fixed;
		width: 100%;
		background-color: ${props => props.theme.primaryColor};

		.branding  {
			display: block;
			opacity: 1;
		}

		.l__row {
			background-color: ${props => props.theme.primaryColor};
			box-shadow: 0 2px 4px 0 hsla(0, 0%, 0%, 0.5);
		}
	}

	@media ${props => props.theme.breakpointLarge} {
		max-height: ${props => props.theme.navbarHeight};
	}
`;

const fadeInDown = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-2rem);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Row = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	animation-name: ${fadeInDown};
	max-height: ${props => props.theme.navbarHeightSmall};
	animation-duration: 1000ms;
	animation-timing-function: ${props => props.theme.easeOutBack};
	animation-fill-mode: forwards;
	animation-delay: 0;
	z-index: 1;
	transition: 300ms ease-in-out;
	position: relative;

	@media ${props => props.theme.breakpointLarge} {
		max-height: ${props => props.theme.navbarHeight};
	}
`;

const Left = styled.div`
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	content: '';
	display: inline-block;
`;

const Center = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	height: 100%;

	@media ${props => props.theme.breakpointLarge} {
		display: none;
	}

	.header-page-title {
		display: none;
		font-size: ${rem('22px')};
		color: ${props => props.theme.white};
		letter-spacing: 1px;
		margin: 0;
	}
`;

const Right = styled.div`
	display: flex;
	align-items: flex-end;
	content: '';
	display: inline-block;
`;

/**
 * Whatever is returned will show up as props
 *
 * @param {any} state
 * @returns
 */
function mapStateToProps(state) {
	return {
		currentPageTitle: state.general.navigation.title,
		isMobileMenuOpen: state.general.navigation.menu,
		profile: state.authentication.profile,
	};
}

export default connect(mapStateToProps)(Header);



// WEBPACK FOOTER //
// ./src/components/ui/navigation/header/index.jsx